/**
 * 清新绿意模板 - 详情页
 * 
 * 设计风格：
 * - 图片放在详情内容下方，无轮播，直接纵向排列
 * - 联系方式直接显示，无需点击查看
 */

import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getListingDetail,
  getListings,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function GreenListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  const { siteConfig } = useSiteConfig();

  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedListings, setRelatedListings] = useState<ListingItem[]>([]);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => setListing(data))
      .catch(() => navigate("/category", { replace: true }))
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  useEffect(() => {
    if (loading || !listing || !siteId) return;
    getListings({ page_size: 6 })
      .then((data) => {
        const filtered = (data.list || []).filter(l => l.id !== id).slice(0, 4);
        setRelatedListings(filtered);
      })
      .catch(console.error);
  }, [loading, listing, id, siteId]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return null;

  const images = listing.images && listing.images.length > 0 ? listing.images : [];
  const hasContact = listing.contact_phone || listing.contact_wechat;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* ── 顶部导航条 ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <nav className="flex items-center gap-1.5 text-sm">
            <Link to="/" className="text-gray-400 hover:text-emerald-600 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <Link to="/category" className="text-gray-400 hover:text-emerald-600 transition-colors">分类</Link>
            {listing.category_name && (
              <>
                <i className="ri-arrow-right-s-line text-xs text-gray-300" />
                <span className="text-gray-400">{listing.category_name}</span>
              </>
            )}
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            <span className="text-emerald-600 font-medium truncate">{listing.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* ── 标题区 ── */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {listing.category_name && (
                  <span className="px-3 py-1 bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-600 text-xs font-semibold rounded-xl border border-emerald-200">
                    {listing.category_name}
                  </span>
                )}
                {listing.is_pinned && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-xl flex items-center gap-1">
                    <i className="ri-pushpin-line" />置顶
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 leading-tight">
                {listing.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm text-gray-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-emerald-400" />
                  {new Date(listing.created_at).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-emerald-400" />
                  {listing.view_count} 次浏览
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-emerald-400" />
                  {listing.contact_name || '匿名用户'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setFavorited(!favorited)}
              className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-2xl transition-all cursor-pointer ${
                favorited ? "bg-red-50 text-red-500" : "bg-gray-50 text-gray-400 hover:text-red-400 hover:bg-red-50"
              }`}
            >
              <i className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`} />
            </button>
          </div>
        </div>

        {/* ── 联系方式 ── 标题下方，直接显示 ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* 发布者 */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-200 flex items-center justify-center text-emerald-600 shrink-0">
              <i className="ri-user-3-line text-xl" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-gray-800">{listing.contact_name || '匿名用户'}</p>
              {siteConfig && listing.company_name && !siteConfig.hide_company_name && (
                <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                  <i className="ri-building-line" />{listing.company_name}
                </p>
              )}
            </div>
          </div>

          {/* 联系方式 - 直接展示 */}
          <div className="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-600 rounded-2xl p-5 text-white shadow-lg shadow-emerald-200">
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2 text-lime-100">
              <i className="ri-customer-service-line" />
              联系方式
            </h3>
            {hasContact ? (
              <div className="space-y-2.5">
                {listing.contact_phone && (
                  <a
                    href={`tel:${listing.contact_phone}`}
                    className="flex items-center justify-between gap-3 py-2.5 px-4 bg-white/15 rounded-xl hover:bg-white/25 transition-colors cursor-pointer group"
                  >
                    <span className="flex items-center gap-2.5">
                      <i className="ri-phone-line text-lime-300 text-lg" />
                      <span className="font-semibold text-sm">{listing.contact_phone}</span>
                    </span>
                    <span className="text-xs bg-lime-300/20 text-lime-200 px-2.5 py-1 rounded-lg shrink-0 group-hover:bg-lime-300/30 transition-colors">
                      <i className="ri-phone-fill mr-1" />拨号
                    </span>
                  </a>
                )}
                {listing.contact_wechat && (
                  <div className="flex items-center justify-between gap-3 py-2.5 px-4 bg-white/15 rounded-xl cursor-pointer group">
                    <span className="flex items-center gap-2.5">
                      <i className="ri-wechat-line text-lime-300 text-lg" />
                      <span className="font-semibold text-sm">{listing.contact_wechat}</span>
                    </span>
                    <span
                      className="text-xs bg-lime-300/20 text-lime-200 px-2.5 py-1 rounded-lg shrink-0 hover:bg-lime-300/30 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(listing.contact_wechat).catch(() => {});
                      }}
                    >
                      <i className="ri-file-copy-line mr-1" />复制
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-white/50 text-center py-2">暂未提供联系方式</p>
            )}
          </div>
        </div>

        {/* ── 详细介绍内容 ── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-gradient-to-b from-emerald-400 to-green-500 rounded-full" />
            详细介绍
          </h2>
          <div className="text-gray-600 leading-relaxed whitespace-pre-wrap">
            {listing.content || "暂无描述信息。"}
          </div>
        </div>

        {/* ── 图片 ── 直接在介绍下方 ── */}
        {images.length > 0 && (
          <div className="space-y-4 mb-6">
            {images.map((img: string, i: number) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
              >
                <img
                  src={img}
                  alt={`${listing.title} - 图${i + 1}`}
                  className="w-full object-contain max-h-[600px]"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {/* 服务区域 */}
        {listing.address && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-8">
            <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <i className="ri-map-pin-line text-emerald-500" />
              服务区域
            </h3>
            <p className="text-gray-500 text-sm">{listing.address}</p>
          </div>
        )}

        {/* ── 发布按钮 ── */}
        <div className="text-center mb-10">
          <Link
            to="/publish"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-emerald-600 font-semibold text-sm rounded-2xl border-2 border-emerald-100 hover:border-emerald-300 hover:bg-emerald-50 transition-all cursor-pointer shadow-sm"
          >
            <i className="ri-add-line" />
            发布类似服务
          </Link>
        </div>

        {/* ── 相关推荐 ── */}
        {relatedListings.length > 0 && (
          <section className="pb-14">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold text-gray-800 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-gradient-to-b from-emerald-400 to-green-500 rounded-full" />
                相关推荐
              </h2>
              <Link to="/category" className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedListings.map(item => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-full h-36 bg-gradient-to-br from-emerald-50 to-green-100 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-2xl text-emerald-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-800 text-sm line-clamp-1 group-hover:text-emerald-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg">{item.category?.name}</span>
                      <span className="text-xs text-gray-400">{formatShortDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 animate-pulse space-y-6">
        <div className="w-3/4 h-8 bg-gray-200 rounded-xl" />
        <div className="w-1/2 h-4 bg-gray-200 rounded" />
        <div className="w-full h-40 bg-gray-200 rounded-2xl" />
        <div className="w-full h-60 bg-gray-200 rounded-2xl" />
      </div>
    </div>
  );
}
